import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PrimaryButton } from '../components';
import AuthService from '../services/AuthService';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const queryToken = searchParams.get('token') || '';
    setToken(queryToken);

    if (!queryToken) {
      setError('Token de redefinição inválido.');
      setValidating(false);
      return;
    }

    const validateToken = async () => {
      try {
        await AuthService.validatePasswordResetToken(queryToken);
        setError(null);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const handleResetPassword = async () => {
    if (newPassword.length < 8) {
      setError('A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await AuthService.resetPassword(token, newPassword);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url("/landing-page-background.png")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          maxWidth: 480,
          borderRadius: '20px',
          backgroundColor: '#1F2C40',
          color: 'white',
          p: { xs: 3, sm: 4 }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            component="img"
            src="/logo192.png"
            alt="ThinkWay"
            sx={{ width: 100, height: 'auto', alignSelf: 'center', mb: 1 }}
          />
          <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center' }}>
            Redefinir senha
          </Typography>

          {validating ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
              <CircularProgress color="secondary" />
              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Validando seu link...
              </Typography>
            </Box>
          ) : success ? (
            <>
              <Alert severity="success">Senha redefinida com sucesso.</Alert>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Sua conta já pode ser acessada com a nova senha.
              </Typography>
              <PrimaryButton onClick={() => navigate('/tw/login')}>
                Ir para o login
              </PrimaryButton>
            </>
          ) : error ? (
            <>
              <Alert severity="error">{error}</Alert>
              <PrimaryButton onClick={() => navigate('/tw/login')}>
                Voltar ao login
              </PrimaryButton>
            </>
          ) : (
            <>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Defina sua nova senha para concluir a recuperação da conta.
              </Typography>
              <TextField
                label="Nova senha"
                type={showNewPassword ? 'text' : 'password'}
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowNewPassword((current) => !current)}
                          edge="end"
                          sx={{ color: 'white' }}
                        >
                          {showNewPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    color: 'white',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              />
              <TextField
                label="Confirmar nova senha"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword((current) => !current)}
                          edge="end"
                          sx={{ color: 'white' }}
                        >
                          {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    color: 'white',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  }
                }}
              />
              <PrimaryButton
                onClick={handleResetPassword}
                disabled={submitting || !newPassword || !confirmPassword}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Salvar nova senha'}
              </PrimaryButton>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ResetPasswordPage;
